import {
  myTasksQueryKey,
  type Task,
  taskByIdQueryKey,
  taskEventsQueryKey,
  taskScheduleIntakeContextOptions,
  tasksQueryKey,
  useCompleteTaskMutation,
} from '@cartwheel/api/v1'
import { Button, SegmentedControl, useToast } from '@cartwheel/components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  AvailabilityNote,
  CanceledNotice,
  ScheduleAppointmentsSection,
  TaskDescription,
} from './shared/task-workspace-primitives'
import { taskAnalyticsContext } from './task-analytics'

type ResolutionMode = 'schedule_appointments' | 'cannot_schedule'

const BODY_TEXT =
  'The referrer and primary guardian will be notified. If no appointment is scheduled within 48 hours, the referral will close automatically.'

function CloseReferralSection({ task, onClose }: { task: Task; onClose: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  const { mutate: complete, isPending } = useCompleteTaskMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myTasksQueryKey() })
      void queryClient.invalidateQueries({ queryKey: tasksQueryKey() })
      void queryClient.invalidateQueries({ queryKey: taskByIdQueryKey({ path: { id: task.id } }) })
      void queryClient.invalidateQueries({
        queryKey: taskEventsQueryKey({ path: { task_id: task.id } }),
      })
      toast.success({ description: 'Referral closure process started' })
      onClose()
    },
    onError: () => {
      toast.error({ description: 'Failed to close referral' })
      setConfirming(false)
    },
  })

  if (confirming) {
    return (
      <div className="rounded-lg border border-brick-light bg-white p-4 space-y-3">
        <p className="text-sm font-semibold leading-[1.5] text-charcoal">Close this referral?</p>
        <p className="text-sm leading-[1.5] text-charcoal-50">{BODY_TEXT}</p>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => setConfirming(false)}
            disabled={isPending}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => complete({ path: { id: task.id }, body: { outcome: 'close_referral' } })}
            disabled={isPending}
            className="flex-1"
            track={{
              name: 'TASK_RESOLUTION_OUTCOME_SELECTED',
              properties: {
                ...taskAnalyticsContext(task),
                resolutionOutcome: 'close_referral',
              },
            }}
          >
            Yes
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-sand bg-white p-4 space-y-3">
      <p className="text-sm font-semibold leading-[1.5] text-charcoal">Close referral</p>
      <p className="text-sm leading-[1.5] text-charcoal-50">{BODY_TEXT}</p>
      <Button variant="danger" onClick={() => setConfirming(true)} className="w-full">
        Close referral
      </Button>
    </div>
  )
}

function ResolutionSection({
  task,
  onClose,
  matchingCliniciansCount,
  caseAssignmentUrl,
}: {
  task: Task
  onClose: () => void
  matchingCliniciansCount?: number | null
  caseAssignmentUrl?: string | null
}) {
  const [mode, setMode] = useState<ResolutionMode>('schedule_appointments')

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Resolution</h3>
        <p className="text-sm leading-[1.5] text-charcoal-50">
          Schedule appointments or log an attempt
        </p>
      </div>

      <SegmentedControl.Root
        value={mode}
        onChange={(value) => setMode(value as ResolutionMode)}
        track={{
          name: 'TASK_RESOLUTION_METHOD_SELECTED',
          properties: ({ value: resolutionMethod }) => ({
            ...taskAnalyticsContext(task),
            resolutionMethod,
          }),
        }}
      >
        <SegmentedControl.Option value="schedule_appointments">
          Schedule appointments
        </SegmentedControl.Option>
        <SegmentedControl.Option value="cannot_schedule">Cannot schedule</SegmentedControl.Option>
      </SegmentedControl.Root>

      {mode === 'schedule_appointments' ? (
        <ScheduleAppointmentsSection
          task={task}
          matchingCliniciansCount={matchingCliniciansCount}
          caseAssignmentUrl={caseAssignmentUrl}
        />
      ) : (
        <CloseReferralSection task={task} onClose={onClose} />
      )}
    </section>
  )
}

export function ScheduleIntakeWorkspace({
  task,
  canWork = true,
  onClose,
}: {
  task: Task
  canWork?: boolean
  onClose: () => void
}) {
  const { data } = useQuery(taskScheduleIntakeContextOptions({ path: { task_id: task.id } }))
  const completionOutcome = task.metadata?.['outcome']

  if (task.status === 'canceled') {
    return (
      <div className="space-y-8">
        <TaskDescription task={task} />
        <CanceledNotice />
      </div>
    )
  }

  if (task.status === 'completed') {
    const completedCopy =
      completionOutcome === 'close_referral'
        ? 'Client has been marked unresponsive. The referrer and primary guardian have been notified. The referral will close in 48 hours if no appointment is scheduled.'
        : 'An intake appointment has been scheduled for this client.'

    return (
      <div className="space-y-8">
        <TaskDescription task={task} />
        <section className="space-y-3">
          <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Completed</h3>
          <div className="rounded-lg border border-sand bg-white p-4 shadow-sm">
            <p className="text-sm leading-[1.5] text-charcoal-50">{completedCopy}</p>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <TaskDescription task={task} />

      {data?.availability_note && <AvailabilityNote note={data.availability_note} />}

      {canWork && (
        <>
          <hr className="border-gray-stroke" />
          <ResolutionSection
            task={task}
            onClose={onClose}
            matchingCliniciansCount={data?.matching_clinicians_count}
            caseAssignmentUrl={data?.case_assignment_url}
          />
        </>
      )}
    </div>
  )
}
